<template>
  <section class="admin-solicitudes py-5">
    <div class="container">
      <h2 class="fw-bold mb-4">Solicitudes de Uso del Sistema</h2>

      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status"></div>
      </div>

      <div class="mb-3 d-flex flex-wrap gap-2 align-items-center">
        <span class="fw-bold me-2">Filtrar por estado:</span>
        <button class="btn btn-sm" :class="estadoFiltro === 'todos' ? 'btn-success' : 'btn-outline-secondary'"
          @click="estadoFiltro = 'todos'">
          Todos
        </button>
        <button v-for="estado in estadosDisponibles" :key="estado" class="btn btn-sm"
          :class="estadoFiltro === estado ? 'btn-success' : 'btn-outline-secondary'" @click="estadoFiltro = estado">
          {{ estado }}
        </button>
      </div>


      <div class="row g-3" v-if="!loading">
  <div
    class="col-md-6 col-lg-4"
    v-for="(solicitud) in solicitudesFiltradas"
    :key="solicitud.id"
  >
    <div class="card h-100 shadow-sm">
      <div class="card-body">
        <h5 class="card-title mb-2">
          {{ solicitud.nombre }}
        </h5>
        <p class="mb-1">
          <strong>Email:</strong> {{ solicitud.email }}
        </p>
        <p class="mb-1">
          <strong>Fecha:</strong> {{ formatFecha(solicitud.fecha) }}
        </p>
        <p class="mb-1">
          <strong>Contacto:</strong> {{ solicitud.preferencia_contacto }}
        </p>
        <p class="mb-2">
          <strong>Estado:</strong>
          <select v-model="solicitud.estado" class="form-select form-select-sm mt-1"
            @change="actualizarEstadoSolicitud(solicitud)">
            <option v-for="estado in estadosDisponibles" :key="estado" :value="estado">
              {{ estado }}
            </option>
          </select>
        </p>
      </div>
      <div class="card-footer text-end">
        <button class="btn btn-sm btn-outline-secondary me-1" @click="abrirModal(solicitud, true)">Ver</button>
        <button class="btn btn-sm btn-primary me-1" @click="abrirModal(solicitud, false)">Editar</button>
        <button class="btn btn-sm btn-danger" @click="eliminarSolicitud(solicitud.id)">Eliminar</button>
      </div>
    </div>
  </div>
</div>


      <!-- Modal -->
      <transition name="fade">
        <div v-if="mostrarModal" class="modal-mask">
          <div class="modal-wrapper">
            <button type="button" class="btn-close float-end" @click="cerrarModal" aria-label="Cerrar"></button>
            <form @submit.prevent="actualizarSolicitud">
              <h5 class="modal-title mb-4">
                {{ modoVer ? 'Ver Solicitud' : 'Editar Solicitud' }}
              </h5>

              <div v-if="solicitudSeleccionada">
                <div class="row g-3">
                  <template v-for="(campo, index) in campos" :key="index">
                    <div :class="`col-md-${campo.cols || 6}`">
                      <label class="form-label">{{ campo.label }}</label>

                      <div v-if="modoVer" class="form-control-plaintext border rounded px-2 py-1 bg-light">
                        {{ solicitudSeleccionada?.[campo.key] || '-' }}
                      </div>

                      <template v-else>
                        <input v-if="!campo.type || campo.type === 'input'" v-model="solicitudSeleccionada[campo.key]"
                          :type="campo.inputType || 'text'" class="form-control" />
                        <textarea v-else-if="campo.type === 'textarea'" v-model="solicitudSeleccionada[campo.key]"
                          class="form-control" :rows="campo.rows || 2" />
                        <select v-else-if="campo.type === 'select'" v-model="solicitudSeleccionada[campo.key]"
                          class="form-select">
                          <option value="email">Email</option>
                          <option value="telefono">Teléfono</option>
                          <option value="red_social">Red Social</option>
                        </select>
                      </template>
                    </div>
                  </template>
                </div>
              </div>

              <div class="mt-4 text-end">
                <button class="btn btn-secondary me-2" type="button" @click="cerrarModal">Cerrar</button>
                <button v-if="!modoVer" class="btn btn-success" type="submit">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      </transition>



    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from '@/axios'

const solicitudes = ref([])
const loading = ref(false)
const solicitudSeleccionada = ref(null)
const modoVer = ref(false)

const estadosDisponibles = ['pendiente', 'en revisión', 'aceptada', 'rechazada']

const campos = [
  { label: 'Nombre', key: 'nombre' },
  { label: 'Email', key: 'email' },
  { label: 'Teléfono', key: 'telefono' },
  { label: 'Red Social', key: 'red_social' },
  { label: 'Organización', key: 'organizacion' },
  { label: 'Preferencia de Contacto', key: 'preferencia_contacto', type: 'select' },
  { label: 'Mensaje', key: 'mensaje', type: 'textarea', cols: 12, rows: 3 }
]

const cargarSolicitudes = async () => {
  loading.value = true
  try {
    const { data } = await axios.get('/api/solicitud_uso')
    solicitudes.value = data
  } catch (err) {
    console.error('Error al cargar solicitudes:', err)
  } finally {
    loading.value = false
  }
}


const mostrarModal = ref(false)

const abrirModal = (solicitud, verSolo = false) => {
  solicitudSeleccionada.value = { ...solicitud }
  modoVer.value = verSolo
  mostrarModal.value = true
}

const cerrarModal = () => {
  mostrarModal.value = false
  solicitudSeleccionada.value = null
}



const actualizarSolicitud = async () => {
  try {
    const id = solicitudSeleccionada.value.id
    await axios.put(`/api/solicitud_uso/${id}`, solicitudSeleccionada.value)
    cerrarModal()
    await cargarSolicitudes()
  } catch (err) {
    console.error('Error al actualizar:', err)
  }
}

const actualizarEstadoSolicitud = async (solicitud) => {
  try {
    await axios.put(`/api/solicitud_uso/${solicitud.id}`, {
      estado: solicitud.estado
    })
  } catch (err) {
    console.error('Error al actualizar estado:', err)
    alert('No se pudo actualizar el estado.')
  }
}


const eliminarSolicitud = async (id) => {
  if (!confirm('¿Eliminar esta solicitud?')) return
  try {
    await axios.delete(`/api/solicitud_uso/${id}`)
    await cargarSolicitudes()
  } catch (err) {
    console.error('Error al eliminar:', err)
  }
}

const formatFecha = (fecha) => {
  return new Date(fecha).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

const estadoFiltro = ref('todos')

const campoOrden = ref(null)
const ordenAscendente = ref(true)

const solicitudesFiltradas = computed(() => {
  let resultado = [...solicitudes.value]

  if (estadoFiltro.value !== 'todos') {
    resultado = resultado.filter(s => s.estado === estadoFiltro.value)
  }

  if (campoOrden.value) {
    resultado.sort((a, b) => {
      const valorA = a[campoOrden.value]?.toString().toLowerCase() || ''
      const valorB = b[campoOrden.value]?.toString().toLowerCase() || ''
      return ordenAscendente.value
        ? valorA.localeCompare(valorB)
        : valorB.localeCompare(valorA)
    })
  }

  return resultado
})


/*const ordenarPor = (campo) => {
  if (campoOrden.value === campo) {
    ordenAscendente.value = !ordenAscendente.value
  } else {
    campoOrden.value = campo
    ordenAscendente.value = true
  }
}*/


onMounted(cargarSolicitudes)
</script>

<style scoped>
.modal-backdrop {
  z-index: 1040;
}

.modal {
  z-index: 1050;
}

.admin-solicitudes {
  background-color: #f9f9f9;
}

.modal-mask {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 40px 20px;
}

.modal-wrapper {
  background: #ffffff;
  border-radius: 12px;
  padding: 30px;
  width: 100%;
  max-width: 700px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  z-index: 10000;
  animation: fadeInUp 0.4s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.cursor-pointer {
  cursor: pointer;
}

th.cursor-pointer:hover {
  text-decoration: underline;
  color: #198754;
}
.card-title {
  font-size: 1.1rem;
  font-weight: 600;
}

</style>
